import {
    Button,
    DataTable,
    DialogHandler,
    EmptyContent,
    EPopupBranding,
    EPopupPosition,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    Header,
    Icon,
    ListPopupModel,
    LoadingContainer,
    PopupsFactory,
    Search,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TOrder,
    TPromisePoolPartialList,
    TPromisePoolRef,
    TPromisePoolReturnData,
    UserModel,
    UsersFactory
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getBasePopup } from "../../../components/beeEditor/baseContent/basePopup"
import { showCreateResourceDialog } from "../../../components/dialogs/createResource"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { listsPromisePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { TPopupsListing } from "../types"
import { getListingColsData, getListingRows } from "../utils"

export default function PopupsListing({ }: TPopupsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<string>("updated_on")
    const [popups, setPopups] = useState<TGenericListReturn<ListPopupModel>>()
    const [loading, setLoading] = useState(false)

    const [users, setUsers] = useState<{ [id: string]: UserModel }>()

    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})
    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    useEffect(() => {
        UsersFactory.list({
            page: 1,
            perPage: 1000
        }).then((res) => {
            const curUsers: { [k: string]: UserModel } = {}
            res.data.forEach((user: UserModel) => {
                curUsers[user.id] = user
            })
            setUsers(curUsers)
        })
    }, [])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(() => {
        fetchPopups()
    }, [page, perPage, order, orderBy, search])

    useEffect(() => {
        return stopPromisePools
    }, [])

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (e) {
            // pass
        }
    }

    function fetchPopups() {
        setLoading(true)
        PopupsFactory.list({
            page,
            perPage,
            name: search || undefined,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            withCount: true
        }).then((data) => {
            const listIds = [...new Set(data.data.map(popup => popup.list_id))].map(id => ({ id }))
            listsPromisePool({ data: listIds, setter: setPartialLists, poolRef: partialListsPromiseRef })

            setPopups(data)
        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleCreatePopup() {
        showCreateResourceDialog({
            titleKey: "general-create-popup",
            addList: true
        }).then(({ btnRef, list, name }) => {
            btnRef.animate()
            return PopupsFactory.create({
                name,
                list_id: list?.id,
                branding: EPopupBranding.basic,
                content: {
                    position: EPopupPosition.center,
                    json: getBasePopup()
                }
            })
        }).then((newPopup) => {
            DialogHandler.hide()
            window.location.href = "#" + fetchRoute({ route: ERoutes.popupDetails, params: { popupId: newPopup.id } })
        }).catch((e) => {
            if (e) {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            }
        })
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".popupsSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number) {
        setPage(cPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleOpenPopup(popup: ListPopupModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.popupDetails, params: { popupId: popup.id } })
    }

    function handleDuplicatePopup(popup: ListPopupModel) {
        PopupsFactory.get({ id: popup.id })
            .then((data) => {
                const newPopup = data.toJson()
                newPopup.name = newPopup.name + ` (${t("general-copy")})`
                delete newPopup.form_id
                return PopupsFactory.create(newPopup)
            })
            .then(() => {
                fetchPopups()
            }).catch((e) => {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            })
    }

    function handleRenamePopup(popup: ListPopupModel) {
        showRenameDialog({ initName: popup.name || "", resourceKey: "general-popup_one" })
            .then(({ value }) => {
                return popup.save({ name: value })
            }).then(() => {
                fetchPopups()
                DialogHandler.hide()
            }).catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action"),
                    })
                }
            })
    }

    function handlePreviewPopup(popup: ListPopupModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.popupDetails, params: { popupId: popup.id } }) + "?preview=true"
    }

    function handleDeletePopup(popup: ListPopupModel) {
        showDeleteDialog({
            name: popup.name || "",
            resourceKey: "general-popup_one",
            validationWordKey: "input-validation-key-delete"
        }).then(({ btnRef }) => {
            btnRef.animate()
            return popup.delete()
        }).then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("popup-deleted")
            })
            fetchPopups()
        }).catch((e) => {
            if (e)
                DialogHandler.show({
                    isExpress: true,
                    type: "warning",
                    title: t("general-error-action")
                })
        })
    }


    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData(), [])

    const rows: any[] = popups && popups.data.map((popup: ListPopupModel, index) => {
        const partialList: Partial<TPromisePoolPartialList> = popup.list_id ? partialLists?.[popup.list_id] : {}
        return getListingRows({
            partialList,
            popup,
            users,
            handleOpenPopup,
            handleDuplicatePopup,
            handleRenamePopup,
            handlePreviewPopup,
            handleDeletePopup
        })
    }) || []

    return <Stack className="popupsListingContainer" gap={8}>
        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t("general-popup_other")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
                headingTooltip={t("popup-heading-tooltip")}
            />}
            right={[
                <Button
                    key="create-popup"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreatePopup}
                    iconOnly
                    disabled={loading}
                ><Icon name="Add18" /></Button>,
                <Button
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("popups-listing-search-placeholder")} initValue={search} className={`popupsSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <LoadingContainer loading={!popups}>
            {popups && popups.data.length === 0 && !search &&
                <EmptyContent
                    title={t("popups-listing-empty-title")}
                    desc={t("popups-listing-empty-desc")}
                    iconName="CreateForm44"
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreatePopup} iconOnly>
                        {t("general-create-popup")}
                    </Button>}
                />
            }

            {popups && popups.data.length === 0 && search &&
                <EmptyContent
                    title={t("popups-listing-empty-search-title")}
                    desc={t("search-empty-desc")}
                />
            }

            {popups && popups.data.length > 0 &&
                <DataTable
                    initState={{
                        rowsPerPage: perPage,
                        order,
                        orderBy
                    }}
                    totalCount={popups?.pagination?.count}
                    className={`popupsDataTable ${loading ? "loading" : ""}`}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                />
            }

        </LoadingContainer>
    </Stack>
}